import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FloppyDisk, ArrowLeft, User, Desktop } from "@phosphor-icons/react";

const PRODUCT_TYPES = [
  "PC Portatile",
  "PC Desktop",
  "All-in-one",
  "Tablet",
  "Smartphone",
  "Stampante",
  "Monitor",
  "Server",
  "Altro",
];

export default function NewInterventionPage() {
  useDocumentTitle("Nuovo intervento");
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [customerMode, setCustomerMode] = useState("new"); // new | existing
  const [customerId, setCustomerId] = useState("");
  const [customerQuery, setCustomerQuery] = useState("");

  const [form, setForm] = useState({
    // customer (new)
    name: "",
    surname: "",
    phone: "",
    email: "",
    address: "",
    // product
    product_type: "PC Portatile",
    product_brand: "",
    product_model: "",
    serial_number: "",
    accessories: "",
    // work
    work_description: "",
    tech_notes: "",
    price_estimate: "",
    cost: "",
  });

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  useEffect(() => {
    if (customerMode !== "existing") return;
    const t = setTimeout(async () => {
      const { data } = await api.get("/customers", { params: customerQuery ? { q: customerQuery } : {} });
      setExistingCustomers(data);
    }, 200);
    return () => clearTimeout(t);
  }, [customerQuery, customerMode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.work_description.trim()) return toast.error("Descrizione lavoro obbligatoria");
    if (customerMode === "new" && (!form.name.trim() || !form.surname.trim() || !form.phone.trim())) {
      return toast.error("Nome, cognome e telefono cliente obbligatori");
    }
    if (customerMode === "existing" && !customerId) return toast.error("Seleziona un cliente");

    setSaving(true);
    try {
      const payload = {
        product_type: form.product_type,
        product_brand: form.product_brand || null,
        product_model: form.product_model || null,
        serial_number: form.serial_number || null,
        accessories: form.accessories || null,
        work_description: form.work_description,
        tech_notes: form.tech_notes || null,
        price_estimate: form.price_estimate ? parseFloat(form.price_estimate) : null,
        cost: form.cost ? parseFloat(form.cost) : null,
      };
      if (customerMode === "existing") {
        payload.customer_id = customerId;
      } else {
        payload.customer = {
          name: form.name.trim(),
          surname: form.surname.trim(),
          phone: form.phone.trim(),
          email: form.email || null,
          address: form.address || null,
        };
      }
      const { data } = await api.post("/interventions", payload);
      toast.success(`Intervento ${data.code} creato`);
      nav(`/interventi/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
              style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft size={14} /> Indietro
      </button>
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// NUOVA SCHEDA</div>
        <h1 className="text-4xl font-display font-bold tracking-tight">Registra intervento</h1>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="new-intervention-form">
        {/* Customer */}
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center gap-2 mb-4">
            <User size={18} weight="duotone" className="text-primary" />
            <h2 className="font-display text-lg font-bold tracking-tight">Cliente</h2>
          </div>
          <div className="flex gap-1 bg-secondary rounded-md p-1 w-fit mb-5">
            {[
              { k: "new", l: "Nuovo cliente" },
              { k: "existing", l: "Cliente esistente" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setCustomerMode(t.k)}
                data-testid={`customer-mode-${t.k}`}
                className={`px-4 py-1.5 rounded text-sm font-medium ${
                  customerMode === t.k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}
              >{t.l}</button>
            ))}
          </div>

          {customerMode === "new" ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Cognome *"><Input required value={form.surname} onChange={on("surname")} data-testid="customer-surname-input" /></Field>
              <Field label="Nome *"><Input required value={form.name} onChange={on("name")} data-testid="customer-name-input" /></Field>
              <Field label="Telefono *"><Input required value={form.phone} onChange={on("phone")} data-testid="customer-phone-input" /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={on("email")} data-testid="customer-email-input" /></Field>
              <div className="sm:col-span-2"><Field label="Indirizzo"><Input value={form.address} onChange={on("address")} data-testid="customer-address-input" /></Field></div>
            </div>
          ) : (
            <div className="space-y-3">
              <Input
                placeholder="Cerca cliente per nome, cognome, telefono…"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                data-testid="existing-customer-search"
              />
              <div className="border border-border/60 rounded-md max-h-64 overflow-y-auto divide-y divide-border/50">
                {existingCustomers.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground text-center">Nessun cliente trovato</div>
                )}
                {existingCustomers.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setCustomerId(c.id)}
                    data-testid={`existing-customer-${c.id}`}
                    className={`w-full text-left p-3 hover:bg-secondary/60 flex justify-between items-center ${
                      customerId === c.id ? "bg-primary/10" : ""
                    }`}
                    style={{ transition: "background-color 0.15s ease" }}
                  >
                    <div>
                      <div className="font-medium">{c.surname} {c.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{c.phone}</div>
                    </div>
                    {customerId === c.id && <span className="font-mono text-xs text-primary">SELEZIONATO</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Product */}
        <Card className="p-6 border-border/70 rounded-lg">
          <div className="flex items-center gap-2 mb-4">
            <Desktop size={18} weight="duotone" className="text-primary" />
            <h2 className="font-display text-lg font-bold tracking-tight">Prodotto</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tipo *">
              <Select value={form.product_type} onValueChange={(v) => setForm((f) => ({ ...f, product_type: v }))}>
                <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Marca"><Input value={form.product_brand} onChange={on("product_brand")} data-testid="product-brand-input" /></Field>
            <Field label="Modello"><Input value={form.product_model} onChange={on("product_model")} data-testid="product-model-input" /></Field>
            <Field label="Numero seriale"><Input value={form.serial_number} onChange={on("serial_number")} data-testid="product-serial-input" className="font-mono" /></Field>
            <div className="sm:col-span-2"><Field label="Accessori consegnati"><Input value={form.accessories} onChange={on("accessories")} placeholder="es. Alimentatore, borsa, mouse…" data-testid="product-accessories-input" /></Field></div>
          </div>
        </Card>

        {/* Work */}
        <Card className="p-6 border-border/70 rounded-lg">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4">Lavoro richiesto</h2>
          <div className="space-y-4">
            <Field label="Descrizione lavoro *">
              <Textarea rows={4} required value={form.work_description} onChange={on("work_description")} data-testid="work-description-input" />
            </Field>
            <Field label="Note tecniche / diagnosi">
              <Textarea rows={3} value={form.tech_notes} onChange={on("tech_notes")} data-testid="tech-notes-input" />
            </Field>
            <Field label="Preventivo — prezzo per il cliente (€)">
              <Input type="number" step="0.01" min="0" value={form.price_estimate} onChange={on("price_estimate")} data-testid="price-estimate-input" className="max-w-xs font-mono" />
            </Field>
            <Field label="Costo — quanto spendi tu per la riparazione (€)">
              <Input type="number" step="0.01" min="0" value={form.cost} onChange={on("cost")} data-testid="cost-input" className="max-w-xs font-mono" />
            </Field>
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav(-1)} data-testid="cancel-btn" className="rounded-full">Annulla</Button>
          <Button type="submit" disabled={saving} data-testid="save-intervention-btn" className="rounded-full gap-2">
            <FloppyDisk size={16} weight="bold" />
            {saving ? "Salvataggio…" : "Salva e genera QR"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
